import React, { useState } from 'react';
import pic5 from '../assets/FusionX2.png';

const LinkInline = ({ href, children, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`text-purple-400 underline hover:text-purple-300 ${className}`}
  >
    {children}
  </a>
);

const moduleData = [
  {
    title: 'Module 1: The Phygital Revolution',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 000 20 14.5 14.5 0 000-20"></path><path d="M2 12h20"></path></svg>',
    sections: [
      {
        heading: 'Objective',
        content:
          'To understand the core problem Play2Visit solves and the massive opportunity it represents.',
      },
      {
        heading: '1.1 The Problem: Rented Attention',
        paragraph:
          'For decades, digital marketing has been about "renting attention." Brands pay huge sums to interrupt potential customers on platforms they don’t own (like social media and search engines). This leads to:',
        list: [
          'Vanishing Engagement: Clicks and impressions are fleeting. The moment the user scrolls past, the engagement is over.',
          'Rising Costs: Ad auctions are increasingly competitive and expensive, making it harder to show a clear ROI.',
          'Ad Fatigue: Consumers are tired of being interrupted and are increasingly blind to traditional digital ads.',
        ],
      },
      {
        heading: '1.2 The Solution: Owned Engagement & The Phygital Bridge',
        paragraphs: [
          'Play2Visit flips the script. Instead of renting attention, we help brands own engagement on their own platforms.',
          'Phygital (Physical + Digital): This is the key concept. It’s about creating a seamless bridge between a brand’s online presence (their app or website) and their physical locations (stores, restaurants, events).',
          'Our Mission: Stop renting attention. Start owning engagement. We empower brands to turn their own digital channels into media that entertains, activates, and, most importantly, converts online players into real-world customers.',
        ],
      },
    ],
  },
  {
    title: 'Module 2: The Play2Visit Platform',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5A2.5 2.5 0 002 11.5v3A2.5 2.5 0 004.5 17H6"></path><path d="M18 9h1.5a2.5 2.5 0 010 5v-3A2.5 2.5 0 0019.5 9H18"></path><line x1="10" y1="15" x2="14" y2="15"></line><line x1="12" y1="13" x2="12" y2="17"></line><path d="M12 20a2 2 0 002-2V6a2 2 0 00-2-2 2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>',
    sections: [
      {
        heading: 'Objective',
        content: 'To learn the core components of a Play2Visit campaign.',
      },
      {
        heading: '2.1 Core Concepts',
        paragraphs: [
          'A Play2Visit campaign is built on three pillars that create a fun and rewarding loop for the player:',
          '1.  FusionX Tournaments:\n    *   What it is: A brand-tailored, playable game experience hosted directly within the client’s app or website. Players compete in weekly tournaments for high scores.\n    *   Why it works: It’s not just a one-off game. The tournament structure, with leaderboards, streaks, and in-game boosters, drives repeat plays and keeps the brand top-of-mind.',
          '2.  In-Store Scoreboards & Rewards:\n    *   What it is: This is the "phygital" magic. When a player visits a client’s physical location, they can unlock special daily rewards and see their name on an exclusive in-store scoreboard. This is often done by scanning a QR code at the location.\n    *   Why it works: It provides a powerful, tangible incentive for players to make a physical visit. It gamifies the act of visiting the store, making it a fun part of the customer journey.',
          '3.  Earned Ad Moments:\n    *   What it is: We believe ads should be invited, not forced. In a Play2Visit campaign, ad views appear at player-chosen moments, such as to unlock a game perk, get an extra life, or check results.\n    *   Why it works: By making ads a voluntary part of the experience, we create a positive brand association. The player feels in control, and the "ad" becomes a valuable part of the game.',
        ],
      },
      {
        heading: '2.1.1 Why Experiencing FusionX is Non-Negotiable',
        paragraphs: [
          'To truly sell Play2Visit, you must *experience* it. Playing FusionX is not just about understanding the game mechanics; it’s about grasping the emotional hooks and the user journey that drives customers into physical stores.',
        ],
        image: <img src={pic5} alt="FusionX Game Screenshot" className="w-full rounded-lg shadow-lg mt-4" />,
        list: [
          'Authentic Storytelling: You cannot genuinely convey the excitement and benefits to a client if you haven’t felt them yourself. Your pitch will be deeper, more confident, and more persuasive.',
          'Product Empathy: Understand the player’s motivation: the thrill of competition, the curiosity about the leaderboard, the tangible reward of an in-store bonus. This empathy helps you tailor your client pitches.',
          'Effective Objection Handling: Clients will ask, "Will it actually be fun for *my* customers?" Having played it, you can answer with authority, providing specific examples and overcoming skepticism.',
          'Strategic Advantage: By engaging with the product as a user, you gain insights into how to best position it for different client types and how to optimize campaigns for maximum foot traffic.',
          'Filtering for Commitment: This small step ensures you’re fully bought into the Play2Visit value proposition, signaling your commitment and setting you apart as a truly invested partner.',
        ],
      },
      {
        heading: 'The Task: Experience FusionX',
        paragraphs: [
          'Your mission is to play the FusionX game for a few times. Aim to reach a score of 30,000 and unlock match-4 bonus once.',
          () => <>You can find the game here: <LinkInline href="https://codespace.fi/fusionx/51-demo.html">Play FusionX Demo</LinkInline> (Open the game on your phone for the authentic experience)</>,
        ],
      },
      {
        heading: '2.2 Technical Overview',
        list: [
          'Easy Integration: The platform is delivered as a simple web embed. This means no heavy development work for your clients. It can be integrated into almost any website or mobile app.',
          'Branded Reskins: We work with the client to "reskin" the game with their branding, colors, and logos, making it feel like a natural extension of their brand.',
        ],
      },
    ],
  },
  {
    title: 'Module 3: The Ideal Customer Profile (ICP)',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>',
    sections: [
      {
        heading: 'Objective',
        content: 'To know who to sell Play2Visit to and what problems to listen for.',
      },
      {
        heading: '3.1 Target Industries',
        list: [
          'Retail: Fashion, electronics, department stores—any retailer with physical locations.',
          'Quick Service Restaurants (QSRs): Coffee shops, fast-food chains, and casual dining.',
          'Hospitality & Events: Hotel chains, music festivals, and sporting events.',
          'Brands with Existing Apps: Any company that has an app and is struggling with user engagement or wants to drive more value from it.',
        ],
      },
      {
        heading: '3.2 Key Pain Points to Listen For',
        list: [
          '"We need to increase our foot traffic."',
          '"Our app downloads are high, but daily active users are low."',
          '"Our social media ad spend is getting expensive, and we can’t prove it leads to in-store sales."',
          '"We want to connect with a younger, tech-savvy audience."',
          '"How can we make our brand more engaging and less transactional?"',
        ],
      },
    ],
  },
  {
    title: 'Module 4: Selling Play2Visit',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"></path></svg>',
    sections: [
      {
        heading: 'Objective',
        content: 'To master the value proposition and confidently sell the platform.',
      },
      {
        heading: '4.1 The Core Value Proposition',
        pullQuote: '"This isn’t about vague impressions. We track the exact number of online players who walk into your store. We call this the Player-to-Visitor Rate, and it’s the most important metric for your business."',
        list: [
          'Measurable ROI: "This isn’t about vague impressions. We track the exact number of online players who walk into your store. We call this the Player-to-Visitor Rate, and it’s the most important metric for your business."',
          'Owned Engagement: "Stop paying to interrupt customers on social media. Build an engaging experience on your own app or website that people want to spend time with."',
          'A Fun Brand Experience: "Turn your brand from something people buy from into something people play with. This builds brand memory and loyalty that lasts long after a campaign ends."',
        ],
      },
      {
        heading: '4.2 Handling Common Objections',
        paragraphs: [
          'Objection: "This seems complicated to set up."\n    *   Response: "It’s actually incredibly simple. It’s a single line of code to embed on your site, and our team provides full support. It’s far easier and more cost-effective than building a custom game from scratch."',
          'Objection: "Is it expensive?"\n    *   Response: "Compared to the volatile and rising costs of ad spend, Play2Visit is a predictable investment. We can model the ROI for you—you’d be surprised how few player-to-visitor conversions you need to make the campaign highly profitable."',
          'Objection: "Will our customers actually play this?"\n    *   Response: "Absolutely. The game mechanics are based on proven principles from the hyper-casual gaming world, which has billions of players. By tailoring it to your brand, we make it feel authentic and fun for your specific audience."',
        ],
      },
    ],
  },
  {
    title: 'Module 5: Your First Campaign',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path></svg>',
    sections: [
      {
        heading: 'Objective',
        content: 'To understand the simple, four-step process for launching a client campaign.',
      },
      {
        heading: 'Steps',
        list: [
          'Client Kick-off: Gather brand assets (logos, colors) and define the campaign goals (e.g., "increase foot traffic by 10%").',
          'Game Configuration: Work with the Play2Visit team to reskin the game and configure tournament details like duration and rewards.',
          'Traffic Strategy: Plan how you’ll drive players to the game. This is crucial! Use QR codes in-store, on packaging, in email newsletters, and through paid social media campaigns.',
          'Launch & Monitor: Go live and watch the results come in on the analytics dashboard. Optimize your traffic strategy based on what’s working best.',
          <img src={pic5} alt="Analytics Dashboard Preview" className="w-full rounded-lg shadow-lg mt-4" />,
        ],
      },
    ],
  },
  {
    title: 'Module 6: Your Path to Certified Partner',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"></circle><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"></path></svg>',
    sections: [
      {
        heading: 'Objective',
        content: 'To understand the clear and lucrative path to building a real revenue stream.',
      },
      {
        heading: 'Overview',
        paragraphs: [
          'The Authorized Reseller tier is your starting point, but the Certified Partner tier is where you build a sustainable, high-profit service line.',
        ],
      },
      {
        heading: '6.1 The Benefits of Certification',
        list: [
          'Massive Pay Increase: Your commission jumps from 15% (first year) to 25% recurring for the lifetime of the client.',
          'Receive Inbound Leads: You get a featured listing in our official Partner Directory.',
          'Expert Status: The "Certified" badge proves your expertise and helps you win more clients.',
          'Dedicated Support: Get priority access to our partner support team.',
        ],
      },
      {
        heading: '6.2 Your Checklist for Success',
        list: [
          'Sign 3 Clients: Get three clients running active campaigns.',
          'Hit a 5% Conversion Rate: Across your clients, ensure at least 5% of players are converting to in-store visitors. We’ll help you with strategies to achieve this.',
          'Retain 80% of Your Clients: Focus on delivering great results so your clients renew and run more campaigns.',
        ],
      },
    ],
  },
];

const TrainingModule = () => {
  const [expanded, setExpanded] = useState(Array(moduleData.length).fill(false));
  const [completed, setCompleted] = useState(Array(moduleData.length).fill(false));

  const toggleModule = (index) => {
    setExpanded((prev) => prev.map((item, i) => (i === index ? !item : item)));
  };

  const markComplete = (index) => {
    setCompleted((prev) => prev.map((item, i) => (i === index ? true : item)));
  };

  const completedCount = completed.filter(Boolean).length;
  const progressPercent = (completedCount / moduleData.length) * 100;

  return (
    <div className="bg-gray-950 text-white p-8 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold">Play2Visit Fundamentals: Partner Training</h1>
          <p className="text-gray-300">
            Welcome to the Play2Visit Partner Program! We're thrilled to have you on board. This training module is your first step towards
            becoming a certified expert in "phygital" marketing and unlocking a significant new revenue stream for your agency.
          </p>
          <p className="text-gray-300">
            Our goal is to get you to the <span className="font-semibold text-purple-300">Certified Partner</span> tier as quickly as possible, where you'll
            earn a <span className="font-semibold text-purple-300">25% recurring lifetime commission</span> on your clients. Think of this initial "Authorized Reseller"
            status as your on-ramp to a lucrative, long-term partnership.
          </p>
          <p className="text-gray-300">Let's begin!</p>
        </div>

        {/* Placeholder Video Embed */}
        <div className="aspect-w-16 aspect-h-9 w-full rounded-lg overflow-hidden shadow-lg hidden">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/dQw4w9WgXcQ"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm uppercase tracking-widest text-purple-300">
            <span>Training Progress</span>
            <span>
              {completedCount} / {moduleData.length} Modules Completed
            </span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-3">
            <div
              className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="space-y-4">
          {moduleData.map((module, index) => (
            <div key={module.title} className="bg-gray-900 border border-white/10 rounded-2xl overflow-hidden">
              <button
                onClick={() => toggleModule(index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="text-purple-300" dangerouslySetInnerHTML={{ __html: module.icon }} />
                  <div>
                    <p className="text-sm uppercase tracking-[0.35em] text-purple-200">Module {index + 1}</p>
                    <p className="text-xl font-semibold">{module.title}</p>
                  </div>
                </div>
                <span className="text-purple-300 text-2xl">{expanded[index] ? '−' : '+'}</span>
              </button>
              {expanded[index] && (
                <div className="px-6 pb-6 space-y-6 text-gray-300">
                  {module.sections.map((section) => (
                    <div key={section.heading} className="space-y-2">
                      <p className="text-lg font-semibold text-white">{section.heading}</p>
                      {section.pullQuote && (
                        <div className="border-l-4 border-purple-400 pl-4 italic text-purple-300">
                          <p className="text-xl">"{section.pullQuote}"</p>
                        </div>
                      )}
                      {section.content && <p>{section.content}</p>}
                      {section.paragraph && <p>{section.paragraph}</p>}
                      {section.paragraphs &&
                        section.paragraphs.map((paragraph, index) => (
                          <p key={index} className="whitespace-pre-line">
                            {typeof paragraph === 'function' ? paragraph() : paragraph}
                          </p>
                        ))}
                      {section.list && (
                        <ul className="list-disc list-inside space-y-1">
                          {section.list.map((item) => (
                            <li key={item} className="whitespace-pre-line">
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.image && <div className="flex justify-center">{section.image}</div>}
                    </div>
                  ))}
                  <button
                    onClick={() => markComplete(index)}
                    disabled={completed[index]}
                    className={`px-5 py-2 rounded-full font-semibold transition ${
                      completed[index]
                        ? 'bg-green-500/80 text-white cursor-default flex items-center gap-2'
                        : 'bg-purple-500/80 hover:bg-purple-600 text-white'
                    }`}
                  >
                    {completed[index] ? (
                      <>
                        <span>Completed</span>
                        <span>✔</span>
                      </>
                    ) : (
                      'Mark as Complete'
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 space-y-3">
          <h2 className="text-2xl font-bold">Final Assessment</h2>
          <p className="text-gray-300">
            You're almost there! The final step is a short, 10-question multiple-choice quiz based on the material you've just reviewed.
            Passing this quiz will officially grant you <span className="font-semibold text-purple-300">Authorized Reseller</span> status and unlock your partner toolkit.
          </p>
          <p className="text-gray-300 font-semibold">Ready to start your journey? <a href="/en/quiz" className="text-purple-400 underline hover:text-purple-300">Let's go!</a></p>
        </div>
      </div>
    </div>
  );
};

export default TrainingModule;
